/**
 * The user-management panel on the admin dashboard.
 *
 * Shows every user in the system with their status badge, a
 * disable/re-enable button (rendered only for active and disabled
 * users — pending_approval users are managed via the requests
 * section instead), a super-admin marker, and their last sign-in.
 *
 * The "disable self" case is handled server-side with a 400, but we
 * also hide the Disable button when the user row matches the
 * currently-signed-in admin — less confusing than letting them
 * click it and see an error.
 */

import type { ReactNode } from "react";

import { useAuth } from "@/features/auth/useAuth";
import { StatusBadge } from "@/features/admin/StatusBadge";
import {
  useAllUsers,
  useChangeUserStatus,
  type AdminUser,
} from "@/features/admin/useUsers";

function formatIsoShort(iso: string | null): string {
  if (!iso) return "—";
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  return date.toLocaleString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export function UsersSection() {
  const query = useAllUsers();

  if (query.isLoading) {
    return <SectionShell>Loading…</SectionShell>;
  }
  if (query.isError) {
    return (
      <SectionShell>
        <div className="text-coral-deep font-sans text-[14px]">
          Couldn't load users. Refresh to retry.
        </div>
      </SectionShell>
    );
  }

  const users = query.data ?? [];

  return (
    <SectionShell>
      {users.length === 0 ? (
        <div className="text-ink-muted font-sans text-[14px] italic">
          No users yet.
        </div>
      ) : (
        <div className="flex flex-col divide-y divide-edge-soft">
          {users.map((user) => (
            <UserRow key={user.id} user={user} />
          ))}
        </div>
      )}
    </SectionShell>
  );
}

function SectionShell({ children }: { children: ReactNode }) {
  return (
    <section aria-labelledby="all-users-heading" className="flex flex-col gap-4">
      <div className="flex items-baseline justify-between">
        <h2 id="all-users-heading" className="font-serif text-display-sm text-ink">
          All <em className="italic font-normal text-coral-deep">users</em>
        </h2>
      </div>
      <div className="rounded-lg border border-edge bg-surface">{children}</div>
    </section>
  );
}

function UserRow({ user }: { user: AdminUser }) {
  const { user: currentAdmin } = useAuth();
  const changeStatus = useChangeUserStatus();

  const isSelf = currentAdmin?.id === user.id;
  const canToggle = !isSelf && (user.status === "active" || user.status === "disabled");
  const nextStatus = user.status === "active" ? "disabled" : "active";
  const toggleLabel = user.status === "active" ? "Disable" : "Re-enable";

  return (
    <div
      data-testid={`user-row-${user.id}`}
      className="flex items-center justify-between gap-4 flex-wrap py-3 px-5"
    >
      <div className="flex flex-col min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-sans text-[15px] font-medium text-ink">
            {user.display_name}
          </span>
          <StatusBadge status={user.status} />
          {user.is_super_admin && (
            <span className="inline-flex items-center px-2 py-[2px] rounded-pill border border-honey-deep/40 bg-honey-soft text-honey-deep font-sans text-[11px] font-semibold uppercase tracking-[0.08em]">
              Super admin
            </span>
          )}
          {isSelf && (
            <span className="font-sans text-[12px] italic text-ink-muted">
              (you)
            </span>
          )}
        </div>
        <div className="font-mono text-[13px] text-ink-muted mt-[2px]">
          {user.email}
        </div>
        <div className="font-sans text-[12px] text-ink-dim mt-[2px]">
          Last sign-in: {formatIsoShort(user.last_sign_in_at)}
        </div>
      </div>

      {canToggle && (
        <button
          type="button"
          disabled={changeStatus.isPending}
          onClick={() =>
            changeStatus.mutate({
              userId: user.id,
              status: nextStatus,
            })
          }
          className="px-4 py-2 rounded-md font-sans text-[13px] font-medium text-ink bg-surface border border-edge hover:border-coral-deep hover:text-coral-deep disabled:opacity-60 focus:outline-none focus-visible:ring-2 focus-visible:ring-amber"
        >
          {changeStatus.isPending ? "Saving…" : toggleLabel}
        </button>
      )}
    </div>
  );
}
